import { Dispatch, MutableRefObject, SetStateAction, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { DatasetContextType } from '../../../types/context';
import { Dataset, GlobalLocationStateType } from '../../../types/types';
import { updateLazyStateFromActiveFilters } from '../../DatasetFormDataContext/utils/updateLazyStateFromActiveFilters';

interface QuickBookmarkFilterRestoreProps {
  dataset: Dataset | undefined;
  datasetId: string | undefined;
  filter: unknown;
  activeFiltersRef: MutableRefObject<Record<string, unknown> | null>;
  setActiveFilters: Dispatch<
    SetStateAction<Record<string, unknown> | null>
  >;
  setLazyState: Dispatch<SetStateAction<Record<string, unknown>>>;
  setPeersetFilterFirstProcessed: Dispatch<SetStateAction<boolean>>;
  activeSorting: any[];
  cancelDatasetData: () => void;
  fetchDatasetData: DatasetContextType['fetchDatasetData'];
}

export const useQuickBookmarkFilterRestore = ({
  dataset,
  datasetId,
  filter,
  activeFiltersRef,
  setActiveFilters,
  setLazyState,
  setPeersetFilterFirstProcessed,
  activeSorting,
  cancelDatasetData,
  fetchDatasetData,
}: QuickBookmarkFilterRestoreProps) => {
  const history = useHistory();
  const { pathname, search, state } = useLocation<GlobalLocationStateType>();

  useEffect(() => {
    if (
      state?.quickBookmarkFilters === undefined ||
      !dataset ||
      dataset.datasetId !== datasetId
    ) {
      return;
    }

    const restoredFilters = JSON.parse(
      JSON.stringify(state.quickBookmarkFilters)
    ) as Record<string, unknown>;

    // The snapshot is the canonical filter state: the grid, detailed-filter
    // dialog and request must all observe the same values. Drop a request
    // started before the restore so its late unfiltered response cannot win.
    cancelDatasetData();
    activeFiltersRef.current = restoredFilters;
    setActiveFilters(restoredFilters);
    setLazyState((oldLazyState) =>
      updateLazyStateFromActiveFilters(
        oldLazyState,
        restoredFilters,
        dataset,
        filter
      )
    );
    // A filter-first dataset must not reopen its filter dialog: this snapshot
    // already represents the values the user previously confirmed.
    setPeersetFilterFirstProcessed(true);

    let disposed = false;
    const consumeNavigationState = () => {
      if (!disposed) {
        history.replace(`${pathname}${search}`, {
          ...state,
          quickBookmarkFilters: undefined,
        });
      }
    };
    void fetchDatasetData(
      false,
      undefined,
      activeSorting,
      restoredFilters
    ).then(consumeNavigationState, consumeNavigationState);

    return () => {
      disposed = true;
    };
  }, [
    activeFiltersRef,
    dataset,
    datasetId,
    history,
    pathname,
    search,
    setActiveFilters,
    setLazyState,
    setPeersetFilterFirstProcessed,
    state,
  ]);
};
